// src/utils/api.js
import axios from 'axios';

// Prediction markets mock data
const predictionMarkets = [
  {
    id: 'market_001',
    title: 'Will Bitcoin reach $50,000 by end of 2024?',
    category: 'Cryptocurrency', 
    description: 'Prediction on whether Bitcoin (BTC) will reach or exceed $50,000 USD by December 31, 2024',
    outcomeTokens: [
      { name: 'Yes', price: 0.67, volume: 125000, shares: 186567 },
      { name: 'No', price: 0.33, volume: 85000, shares: 257575 }
    ],
    totalVolume: 210000,
    liquidity: 45000,
    endDate: '2024-12-31',
    status: 'active',
    image: '/assets/images/btc-prediction.png'
  },
  {
    id: 'market_002',
    title: 'US Presidential Election 2024 Winner',
    category: 'Politics',
    description: 'Who will win the 2024 United States Presidential Election?',
    outcomeTokens: [
      { name: 'Democratic Candidate', price: 0.52, volume: 890000, shares: 1711538 },
      { name: 'Republican Candidate', price: 0.48, volume: 820000, shares: 1708333 }
    ],
    totalVolume: 1710000,
    liquidity: 125000,
    endDate: '2024-11-05',
    status: 'active',
    image: '/assets/images/election-2024.png'
  },
  {
    id: 'market_003',
    title: 'Will Tesla stock reach $300 in 2024?',
    category: 'Stocks',
    description: 'Prediction on whether Tesla (TSLA) stock will reach or exceed $300 per share at any point in 2024',
    outcomeTokens: [
      { name: 'Yes', price: 0.31, volume: 65000, shares: 209677 },
      { name: 'No', price: 0.69, volume: 145000, shares: 210144 }
    ],
    totalVolume: 210000,
    liquidity: 28000,
    endDate: '2024-12-31',
    status: 'active',
    image: '/assets/images/tesla-stock.png'
  },
  {
    id: 'market_004',
    title: 'Will AI reach AGI by 2025?',
    category: 'Technology',
    description: 'Will Artificial General Intelligence (AGI) be achieved by any organization by the end of 2025?',
    outcomeTokens: [
      { name: 'Yes', price: 0.15, volume: 45000, shares: 300000 },
      { name: 'No', price: 0.85, volume: 195000, shares: 229411 }
    ],
    totalVolume: 240000,
    liquidity: 18000,
    endDate: '2025-12-31',
    status: 'active',
    image: '/assets/images/agi-prediction.png'
  },
  {
    id: 'market_005',
    title: 'Climate: Will 2024 be hottest year on record?',
    category: 'Climate',
    description: 'Will 2024 be officially recorded as the hottest year in global temperature records?',
    outcomeTokens: [
      { name: 'Yes', price: 0.74, volume: 89000, shares: 120270 },
      { name: 'No', price: 0.26, volume: 32000, shares: 123077 }
    ],
    totalVolume: 121000,
    liquidity: 15500,
    endDate: '2025-01-31',
    status: 'active',
    image: '/assets/images/climate-2024.png'
  },
  {
    id: 'market_006',
    title: 'Will SpaceX land humans on Mars by 2030?',
    category: 'Space',
    description: 'Will SpaceX successfully land human astronauts on Mars by December 31, 2030?',
    outcomeTokens: [
      { name: 'Yes', price: 0.22, volume: 78000, shares: 354545 },
      { name: 'No', price: 0.78, volume: 278000, shares: 356410 }
    ],
    totalVolume: 356000,
    liquidity: 42000,
    endDate: '2030-12-31',
    status: 'active',
    image: '/assets/images/spacex-mars.png'
  }
];

// Function to get prediction markets data
export const getPredictionMarkets = async (category = 'all', limit = 20) => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    let markets = [...predictionMarkets];
    
    // Filter by category if specified
    if (category !== 'all') {
      markets = markets?.filter(market => 
        market?.category?.toLowerCase() === category?.toLowerCase()
      );
    }
    
    // Limit results
    if (limit) {
      markets = markets?.slice(0, limit);
    }
    
    // Add slight price fluctuations for realism
    markets = markets?.map(market => ({
      ...market,
      outcomeTokens: market?.outcomeTokens?.map(token => ({
        ...token,
        price: Math.max(0.01, Math.min(0.99, 
          token?.price + (Math.random() - 0.5) * 0.02
        ))
      }))
    }));
    
    return {
      success: true,
      data: markets,
      total: markets?.length
    };
  } catch (error) {
    console.error('Error fetching prediction markets:', error);
    return {
      success: false,
      error: error?.message,
      data: []
    };
  }
};

// Function to get specific market details
export const getMarketDetails = async (marketId) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const market = predictionMarkets?.find(m => m?.id === marketId);
    if (!market) {
      throw new Error('Market not found');
    }
    
    // Add additional details for specific market view
    const detailedMarket = {
      ...market,
      priceHistory: generatePriceHistory(),
      recentTrades: generateRecentTrades(),
      marketMakers: generateMarketMakers()
    };
    
    return {
      success: true,
      data: detailedMarket
    };
  } catch (error) {
    console.error('Error fetching market details:', error);
    return {
      success: false,
      error: error?.message,
      data: null
    };
  }
};

// Function to place a bet
export const placeBet = async (marketId, outcome, amount, price) => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Validate inputs
    if (!marketId || !outcome || !amount || !price) {
      throw new Error('Missing required bet parameters');
    }
    
    if (amount < 1) {
      throw new Error('Minimum bet amount is $1');
    }
    
    if (price < 0.01 || price > 0.99) {
      throw new Error('Price must be between $0.01 and $0.99');
    }
    
    const betId = `bet_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    
    return {
      success: true,
      data: {
        betId,
        marketId,
        outcome,
        amount,
        price,
        shares: Math.floor(amount / price),
        timestamp: new Date()?.toISOString(),
        status: 'confirmed'
      }
    };
  } catch (error) {
    console.error('Error placing bet:', error);
    return {
      success: false,
      error: error?.message,
      data: null
    };
  }
};

// Function to create a new market
export const createMarket = async (marketData) => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Validate required fields
    if (!marketData?.title || !marketData?.description || !marketData?.category || !marketData?.endDate) {
      throw new Error('Missing required market fields');
    }
    
    // Generate unique market ID
    const marketId = `market_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    
    // Create market object
    const newMarket = {
      id: marketId,
      title: marketData?.title,
      category: marketData?.category,
      description: marketData?.description,
      outcomeTokens: [
        { name: 'Yes', price: 0.50, volume: 0, shares: 0 },
        { name: 'No', price: 0.50, volume: 0, shares: 0 }
      ],
      totalVolume: 0,
      liquidity: 0,
      endDate: marketData?.endDate,
      endTime: marketData?.endTime,
      timezone: marketData?.timezone,
      status: marketData?.status || 'active',
      createdAt: marketData?.createdAt || new Date()?.toISOString(),
      creatorAddress: '0x' + Math.random()?.toString(16)?.substr(2, 40),
      minBetAmount: marketData?.minBetAmount || 1,
      maxBetAmount: marketData?.maxBetAmount || 10000,
      creatorFee: marketData?.creatorFee || 2,
      resolutionSource: marketData?.resolutionSource,
      resolutionCriteria: marketData?.resolutionCriteria,
      isPublic: marketData?.isPublic !== false,
      allowEarlyResolution: marketData?.allowEarlyResolution || false,
      tags: marketData?.tags || [],
      image: `/assets/images/market-${marketId}.png`
    };
    
    // Add to mock database (in real app this would be sent to API)
    predictionMarkets?.push(newMarket);
    
    return {
      success: true,
      data: newMarket,
      message: 'Market created successfully'
    };
  } catch (error) {
    console.error('Error creating market:', error);
    return {
      success: false,
      error: error?.message,
      data: null
    };
  }
};

// Function to save draft market
export const saveDraftMarket = async (marketData) => {
  try {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // In real app, this would save to user's drafts
    const draftId = `draft_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    
    const draftMarket = {
      id: draftId,
      ...marketData,
      status: 'draft',
      lastModified: new Date()?.toISOString()
    };
    
    // Save to localStorage as example
    const existingDrafts = JSON.parse(localStorage.getItem('marketDrafts') || '[]');
    const updatedDrafts = [...existingDrafts?.filter(draft => draft?.title !== marketData?.title), draftMarket];
    localStorage.setItem('marketDrafts', JSON.stringify(updatedDrafts));
    
    return {
      success: true,
      data: draftMarket,
      message: 'Draft saved successfully'
    };
  } catch (error) {
    console.error('Error saving draft:', error);
    return {
      success: false,
      error: error?.message,
      data: null
    };
  }
};

// Function to get user's draft markets
export const getDraftMarkets = async () => {
  try {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const drafts = JSON.parse(localStorage.getItem('marketDrafts') || '[]');
    
    return {
      success: true,
      data: drafts,
      total: drafts?.length
    };
  } catch (error) {
    console.error('Error fetching drafts:', error);
    return {
      success: false,
      error: error?.message,
      data: []
    };
  }
};

// Generate mock price history for charts
const generatePriceHistory = () => {
  const history = [];
  const days = 30;
  let currentPrice = 0.5 + Math.random() * 0.3; // Start between 0.5-0.8
  
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date?.setDate(date?.getDate() - i);
    
    // Add some random walk
    currentPrice += (Math.random() - 0.5) * 0.05;
    currentPrice = Math.max(0.05, Math.min(0.95, currentPrice));
    
    history.push({
      date: date?.toISOString(),
      yesPrice: currentPrice,
      noPrice: 1 - currentPrice,
      volume: Math.floor(Math.random() * 10000) + 1000
    });
  }
  
  return history;
};

// Generate mock recent trades
const generateRecentTrades = () => {
  const trades = [];
  const outcomes = ['Yes', 'No'];
  
  for (let i = 0; i < 20; i++) {
    const outcome = outcomes?.[Math.floor(Math.random() * outcomes?.length)];
    const price = Math.random() * 0.8 + 0.1; // 0.1 to 0.9
    const shares = Math.floor(Math.random() * 1000) + 10;
    
    trades?.push({
      id: `trade_${i}`,
      outcome,
      price: price?.toFixed(2),
      shares,
      total: (price * shares)?.toFixed(2),
      timestamp: new Date(Date.now() - Math.random() * 86400000)?.toISOString(),
      type: Math.random() > 0.5 ? 'buy' : 'sell'
    });
  }
  
  return trades?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

// Generate mock market makers data
const generateMarketMakers = () => {
  return [
    { address: '0x1234...5678', volume: 125000, trades: 1250 },
    { address: '0xabcd...efgh', volume: 89000, trades: 890 },
    { address: '0x9876...4321', volume: 67000, trades: 670 }
  ];
};

// Create a regular axios instance for other API calls
const api = axios?.create({
  baseURL: import.meta.env?.VITE_API_BASE_URL || 'https://api.predictionmarkets.com/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000
});

// Add authorization header to requests if user is logged in
api?.interceptors?.request?.use(config => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Error handling interceptor
api?.interceptors?.response?.use(
  response => response,
  error => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
function getLatestPrices(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getLatestPrices is not implemented yet.', args);
  return null;
}

export { getLatestPrices };
function formatPriceData(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: formatPriceData is not implemented yet.', args);
  return null;
}

export { formatPriceData };